import { useMemo, useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { TopbarCalendarIcon } from '@/components/icons/topbar'
import { cn } from '@/lib/utils'

function currentPeriod(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function formatPeriodLabel(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString(locale, {
    month: 'short',
    year: 'numeric',
  })
}

function parsePeriod(period: string): { year: number; month: number } {
  const [year, month] = period.split('-').map(Number)
  return { year, month }
}

function toPeriod(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

function isMonthDisabled(
  year: number,
  month: number,
  maxYear: number,
  maxMonth: number
): boolean {
  return year > maxYear || (year === maxYear && month > maxMonth)
}

export interface MonthPickerProps {
  value: string
  onChange: (value: string) => void
  id?: string
  className?: string
}

const navBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-[#e7e9ef] bg-white text-[#171a20] outline-none transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40'

// Month grid picker — TopNavi trigger (Figma 863:37091); dropdown styled from
// product screenshot (3×4 bordered month cells + year chevrons).
export function MonthPicker({
  value,
  onChange,
  id = 'month-picker',
  className,
}: MonthPickerProps) {
  const { t, i18n } = useTranslation('shell')
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(() => parsePeriod(value).year)

  const label = formatPeriodLabel(value, i18n.language)
  const { year: selectedYear, month: selectedMonth } = parsePeriod(value)
  const { year: maxYear, month: maxMonth } = parsePeriod(currentPeriod())

  const monthLabels = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Date(viewYear, i, 1).toLocaleDateString(i18n.language, {
          month: 'short',
        })
      ),
    [viewYear, i18n.language]
  )

  const selectMonth = (month: number) => {
    onChange(toPeriod(viewYear, month))
    setOpen(false)
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (next) setViewYear(parsePeriod(value).year)
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          id={id}
          className={cn(
            'flex h-10 shrink-0 items-center gap-2 rounded-xl border border-[#e7e9ef] bg-white px-4 py-3',
            'font-sans text-sm font-medium leading-[1.4] text-[#171a20]',
            'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className
          )}
          aria-label={`${t('monthPicker.month')}: ${label}`}
        >
          <TopbarCalendarIcon className='size-4 text-[#ff5a1f]' />
          <span className='whitespace-nowrap'>{label}</span>
          <ChevronDown className='size-4 shrink-0 text-[#171a20]' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='w-[280px] overflow-hidden rounded-xl border border-[#e7e9ef] p-0 shadow-md'
      >
        <div className='flex items-center justify-between gap-2 px-3 pt-3'>
          <button
            type='button'
            className={navBtnClass}
            aria-label={t('monthPicker.prevYear')}
            onClick={() => setViewYear(y => y - 1)}
          >
            <ChevronLeft className='size-4' />
          </button>
          <span className='text-base font-semibold leading-none text-[#171a20]'>
            {viewYear}
          </span>
          <button
            type='button'
            className={navBtnClass}
            disabled={viewYear >= maxYear}
            aria-label={t('monthPicker.nextYear')}
            onClick={() => setViewYear(y => y + 1)}
          >
            <ChevronRight className='size-4' />
          </button>
        </div>

        <div className='grid grid-cols-3 gap-2 p-3'>
          {monthLabels.map((monthLabel, i) => {
            const month = i + 1
            const disabled = isMonthDisabled(
              viewYear,
              month,
              maxYear,
              maxMonth
            )
            const selected =
              selectedYear === viewYear && selectedMonth === month
            return (
              <button
                key={month}
                type='button'
                disabled={disabled}
                onClick={() => selectMonth(month)}
                className={cn(
                  'rounded-md border px-2 py-2.5 text-sm font-normal leading-none transition-colors',
                  selected &&
                    'border-primary bg-primary font-semibold text-primary-foreground',
                  !selected &&
                    !disabled &&
                    'border-[#e7e9ef] bg-white text-[#171a20] hover:bg-accent',
                  disabled &&
                    'cursor-not-allowed border-[#e7e9ef] bg-white text-muted-foreground opacity-40'
                )}
              >
                {monthLabel}
              </button>
            )
          })}
        </div>

        <div className='border-t border-[#e7e9ef] px-3 py-2'>
          <button
            type='button'
            className='w-full rounded-md py-1.5 text-sm font-normal text-primary hover:bg-accent'
            onClick={() => {
              onChange(currentPeriod())
              setOpen(false)
            }}
          >
            {t('monthPicker.thisMonth')}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default MonthPicker
